import React from 'react';
import styles from './McpBrandMark.module.css';

/** Catalog providers are recognisable by colour, so known ids keep their brand hue. */
const BRAND_COLORS: Record<string, string> = {
  linear: '#5e6ad2',
  notion: '#1f2937',
  'google-drive': '#1fa463',
  gmail: '#ea4335',
  'google-calendar': '#4285f4',
  atlassian: '#0052cc',
  slack: '#4a154b',
  intercom: '#1f8ded',
  zendesk: '#03363d',
  hubspot: '#e8552d',
  stripe: '#635bff',
  paddle: '#f43d7a',
  'paddle-sandbox': '#f43d7a',
  airtable: '#2d7ff9',
  asana: '#d64550',
  clickup: '#7b68ee',
  monday: '#f62b54',
  paypal: '#00457c',
  shopify: '#5e8e3e',
  github: '#24292f',
};

/*
 * Custom servers have no brand, so they borrow a hue from their id. The list is
 * kept wide and spread across the colour wheel because a short palette puts
 * two identical tiles next to each other in a rail of only a few servers.
 * Every entry is dark enough to carry white initials.
 */
const FALLBACK_COLORS = [
  '#2563eb',
  '#be123c',
  '#0f766e',
  '#b45309',
  '#7c3aed',
  '#0369a1',
  '#c2410c',
  '#4d7c0f',
  '#a21caf',
  '#0891b2',
  '#9f1239',
  '#4338ca',
  '#15803d',
  '#7e22ce',
];

const hashString = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) % 100000;
  }
  return hash;
};

export const getBrandInitials = (name: string): string => {
  const words = name
    .split(/[\s/&-]+/)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);

  if (words.length === 0) {
    return '?';
  }

  if (words.length === 1) {
    return words[0].slice(0, 1).toUpperCase();
  }

  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

export const getBrandColor = (id: string, name: string): string => {
  const known = BRAND_COLORS[id.trim().toLowerCase()];
  if (known) {
    return known;
  }

  return FALLBACK_COLORS[hashString(id || name) % FALLBACK_COLORS.length];
};

type McpBrandMarkProps = {
  id: string;
  name: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
};

export const McpBrandMark: React.FC<McpBrandMarkProps> = ({
  id,
  name,
  size = 'md',
  className,
}) => (
  <span
    className={[styles.mark, styles[size], className].filter(Boolean).join(' ')}
    style={{ backgroundColor: getBrandColor(id, name) }}
    aria-hidden="true"
  >
    {getBrandInitials(name)}
  </span>
);
